/**
 * Checks if the input is empty, null or undefined
 * @param input stringish input
 * @returns true if input is empty, null or undefined
 */
export const isEmptyOrNull = (
  input?: number | string | null
): input is null | undefined =>
  input === undefined || !input?.toString().trim();

/**
 * Returns fallback if input is empty, null or undefined
 * @param fallback fallback
 * @param input stringish input
 * @returns input or fallback if input is empty, null or undefined
 */
export const emptyFallback = (
  fallback: string,
  input?: number | string | null
): string => {
  if (isEmptyOrNull(input)) {
    return fallback;
  }
  return input.toString();
};

/**
 * Retrieves the initials of a string containing two names.
 *
 * @param firstName - first name.
 * @param lastName - last name.
 * @returns the initials of the two names in uppercase.
 * @throws if the input does not contain at least two names.
 */
export const getInitials = (firstName?: string, lastName?: string): string => {
  let initials = '';
  // ggf. noch Whitespaces vorher entfernen
  if (!isEmptyOrNull(firstName)) initials += firstName.trim()[0];
  if (!isEmptyOrNull(lastName)) initials += lastName.trim()[0];

  return initials.toUpperCase();
};

/**
 * Capitalizes the first letter of a name, keeping the rest as-is.
 */
const capitalizeFirst = (name: string): string =>
  name.charAt(0).toUpperCase() + name.slice(1);

/**
 * Builds the "public name" shown for a person: first name plus the initial of
 * the last name (e.g. "Max M."). Both the first name and the last-name initial
 * start with an uppercase letter. Falls back to just the first name when the
 * last name is missing, and to an empty string when both are missing (the
 * caller decides on a placeholder). Whitespace-robust.
 *
 * @param firstName - first name.
 * @param lastName - last name.
 * @returns the public name, or an empty string when no name is available.
 */
export const getPublicName = (
  firstName?: string,
  lastName?: string
): string => {
  if (isEmptyOrNull(firstName)) {
    return isEmptyOrNull(lastName)
      ? ''
      : `${lastName.trim().charAt(0).toUpperCase()}.`;
  }
  const first = capitalizeFirst(firstName.trim());
  if (isEmptyOrNull(lastName)) return first;
  return `${first} ${lastName.trim().charAt(0).toUpperCase()}.`;
};

export const objectToString = (
  obj: object | null | undefined
): string | undefined => {
  if (!obj) return;
  if (typeof obj === 'string') return obj;
  return JSON.stringify(obj);
};
